import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch,
  increment,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from '../firebase';
import { createGLTransaction, GLTransaction, GLTransactionEntry } from './glTransactionServices';
import { getGLAccountById } from './glAccountServices';

// Product Cost interface
export interface ProductCost {
  id?: string;
  productId: string;
  productName: string;
  productSku: string;
  averageCost: number;
  lastPurchasePrice: number;
  lastPurchaseDate: Date | Timestamp;
  costHistory: CostHistoryEntry[];
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

// Cost History Entry interface
export interface CostHistoryEntry {
  date: Date | Timestamp;
  purchasePrice: number;
  quantity: number;
  averageCostAfter: number;
  receiptId: string;
}

// Purchase Receipt interface
export interface PurchaseReceipt {
  id?: string;
  vendorId: string;
  vendorName: string;
  receiptNumber: string;
  receiptDate: Date | Timestamp;
  totalAmount: number;
  paymentStatus: 'paid' | 'unpaid' | 'partial';
  items: PurchaseReceiptItem[];
  notes: string;
  createdBy: string;
  glTransactionId?: string;
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

// Purchase Receipt Item interface
export interface PurchaseReceiptItem {
  productId: string;
  productName: string;
  productSku: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
}

// Get product cost data
export const getProductCost = async (productId: string): Promise<ProductCost | null> => {
  try {
    const productCostRef = doc(db, 'productCosts', productId);
    const productCostSnap = await getDoc(productCostRef);
    
    if (productCostSnap.exists()) {
      const data = productCostSnap.data() as Omit<ProductCost, 'id'>;
      return {
        id: productCostSnap.id,
        ...data
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting product cost:', error);
    throw error;
  }
};

// Create or update product cost
export const updateProductCost = async (
  productId: string, 
  data: Partial<Omit<ProductCost, 'id' | 'productId' | 'createdAt' | 'updatedAt'>>
): Promise<string> => {
  try {
    const productCostRef = doc(db, 'productCosts', productId);
    const productCostSnap = await getDoc(productCostRef);
    
    if (productCostSnap.exists()) {
      // Update existing product cost
      await updateDoc(productCostRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      return productId;
    } else {
      // Create new product cost
      await setDoc(productCostRef, {
        productId,
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return productId;
    }
  } catch (error) {
    console.error('Error updating product cost:', error);
    throw error;
  }
};

// Calculate and update average cost
export const updateAverageCost = async (
  productId: string,
  productName: string,
  productSku: string,
  newPurchaseQuantity: number,
  newPurchasePrice: number,
  receiptId: string,
  currentQuantity: number
): Promise<number> => {
  try {
    // Get current product cost data
    const productCost = await getProductCost(productId);
    
    // Calculate new average cost
    let currentAverageCost = 0;
    if (productCost) {
      currentAverageCost = productCost.averageCost;
    }
    
    const currentTotalValue = currentQuantity * currentAverageCost;
    const newPurchaseValue = newPurchaseQuantity * newPurchasePrice;
    const newTotalQuantity = currentQuantity + newPurchaseQuantity;
    
    // Handle manual additions with zero cost properly
    // If this is a manual addition with zero cost, it should dilute the average cost
    // For example: 20 units at RM 200 each + 2 free units = 22 units at RM 181.82 each
    let newAverageCost;
    
    if (newPurchasePrice === 0 && newPurchaseQuantity > 0) {
      // This is a free product addition (manual adjustment with no cost)
      // The total value stays the same, but quantity increases
      newAverageCost = newTotalQuantity > 0 
        ? currentTotalValue / newTotalQuantity 
        : 0;
    } else {
      // Normal purchase with cost
      newAverageCost = newTotalQuantity > 0 
        ? (currentTotalValue + newPurchaseValue) / newTotalQuantity
        : newPurchasePrice; // If no inventory, use new purchase price
    }
    
    // Prepare cost history entry
    const costHistoryEntry: CostHistoryEntry = {
      date: new Date(),
      purchasePrice: newPurchasePrice,
      quantity: newPurchaseQuantity,
      averageCostAfter: newAverageCost,
      receiptId
    };
    
    // Update product cost record
    await updateProductCost(productId, {
      productName,
      productSku,
      averageCost: newAverageCost,
      lastPurchasePrice: newPurchasePrice,
      lastPurchaseDate: new Date(),
      costHistory: productCost 
        ? [...productCost.costHistory, costHistoryEntry]
        : [costHistoryEntry]
    });
    
    return newAverageCost;
  } catch (error) {
    console.error('Error updating average cost:', error);
    throw error;
  }
};

// Create purchase receipt
export const createPurchaseReceipt = async (
  receipt: Omit<PurchaseReceipt, 'id' | 'createdAt' | 'updatedAt'>,
  inventoryAccountId: string,
  apAccountId: string,
  currentQuantities: Record<string, number>
): Promise<string> => {
  try {
    const batch = writeBatch(db);
    
    // Create receipt document
    const receiptRef = doc(collection(db, 'purchaseReceipts'));
    const receiptData = {
      ...receipt,
      receiptDate: receipt.receiptDate instanceof Date 
        ? Timestamp.fromDate(receipt.receiptDate) 
        : receipt.receiptDate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    batch.set(receiptRef, receiptData);
    
    // Prepare GL transaction entries
    const glEntries: GLTransactionEntry[] = [];
    
    // Get inventory and AP accounts
    const inventoryAccount = await getGLAccountById(inventoryAccountId);
    const apAccount = await getGLAccountById(apAccountId);
    
    if (!inventoryAccount || !apAccount) {
      throw new Error('Required GL accounts not found');
    }
    
    // Add inventory account entry (debit)
    glEntries.push({
      accountId: inventoryAccountId,
      accountNumber: inventoryAccount.accountNumber,
      accountName: inventoryAccount.accountName,
      debit: receipt.totalAmount,
      credit: 0
    });
    
    // Add accounts payable entry (credit)
    glEntries.push({
      accountId: apAccountId,
      accountNumber: apAccount.accountNumber,
      accountName: apAccount.accountName,
      debit: 0,
      credit: receipt.totalAmount
    });
    
    // Create GL transaction
    const glTransaction: Omit<GLTransaction, 'id'> = {
      transactionDate: receipt.receiptDate,
      description: `Purchase Receipt #${receipt.receiptNumber} - ${receipt.vendorName}`,
      referenceType: 'purchase_receipt',
      referenceId: receiptRef.id,
      entries: glEntries
    };
    
    await createGLTransaction(glTransaction);
    
    // Update product costs and average costs
    for (const item of receipt.items) {
      const currentQuantity = currentQuantities[item.productId] || 0;
      await updateAverageCost(
        item.productId,
        item.productName,
        item.productSku,
        item.quantity,
        item.unitCost,
        receiptRef.id,
        currentQuantity
      );
    }
    
    // Commit batch
    await batch.commit();
    
    return receiptRef.id;
  } catch (error) {
    console.error('Error creating purchase receipt:', error);
    throw error;
  }
};

// Get purchase receipts
export const getPurchaseReceipts = async (
  limitCount?: number,
  startAfterDate?: Date | Timestamp
): Promise<PurchaseReceipt[]> => {
  try {
    let q = query(
      collection(db, 'purchaseReceipts'),
      orderBy('receiptDate', 'desc')
    );
    
    if (startAfterDate) {
      const startAfterTimestamp = startAfterDate instanceof Date ? Timestamp.fromDate(startAfterDate) : startAfterDate;
      q = query(q, startAfter(startAfterTimestamp));
    }
    
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    
    const querySnapshot = await getDocs(q);
    const receipts: PurchaseReceipt[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Omit<PurchaseReceipt, 'id'>;
      receipts.push({
        id: doc.id,
        ...data
      });
    });
    
    return receipts;
  } catch (error) {
    console.error('Error getting purchase receipts:', error);
    throw error;
  }
};

// Get purchase receipt by ID
export const getPurchaseReceiptById = async (receiptId: string): Promise<PurchaseReceipt | null> => {
  try {
    const receiptRef = doc(db, 'purchaseReceipts', receiptId);
    const receiptSnap = await getDoc(receiptRef);
    
    if (receiptSnap.exists()) {
      const data = receiptSnap.data() as Omit<PurchaseReceipt, 'id'>;
      return {
        id: receiptSnap.id,
        ...data
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting purchase receipt:', error);
    throw error;
  }
};

// Update purchase receipt with GL transaction ID
export const updatePurchaseReceiptWithGLTransaction = async (receiptId: string, glTransactionId: string): Promise<void> => {
  try {
    const receiptRef = doc(db, 'purchaseReceipts', receiptId);
    await updateDoc(receiptRef, {
      glTransactionId: glTransactionId,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating purchase receipt with GL transaction ID:', error);
    throw error;
  }
};

// Remove GL transaction ID from purchase receipt
export const removePurchaseReceiptGLTransaction = async (receiptId: string): Promise<void> => {
  try {
    const receiptRef = doc(db, 'purchaseReceipts', receiptId);
    await updateDoc(receiptRef, {
      glTransactionId: null,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error removing GL transaction ID from purchase receipt:', error);
    throw error;
  }
};

// Get all product costs
export const getAllProductCosts = async (): Promise<ProductCost[]> => {
  try {
    const q = query(collection(db, 'productCosts'));
    const querySnapshot = await getDocs(q);
    const productCosts: ProductCost[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Omit<ProductCost, 'id'>;
      productCosts.push({
        id: doc.id,
        ...data
      });
    });
    
    return productCosts;
  } catch (error) {
    console.error('Error getting all product costs:', error);
    throw error;
  }
};

// Note: Inventory adjustment functionality has been removed as it's handled by the main app

// Get inventory valuation
export const getInventoryValuation = async (
  year?: number,
  month?: number
): Promise<{
  totalCost: number;
  items: Array<{
    productId: string;
    productName: string;
    productSku: string;
    quantity: number;
    averageCost: number;
    totalValue: number;
  }>;
}> => {
  try {
    // Set default year and month to current if not provided
    const currentDate = new Date();
    const selectedYear = year !== undefined ? year : currentDate.getFullYear();
    const selectedMonth = month !== undefined ? month : currentDate.getMonth();
    
    // Get all active products from the products collection
    let productsQuery = query(collection(db, 'products'), where('active', '==', true));
    const productsSnapshot = await getDocs(productsQuery);
    const products: Array<any> = [];
    
    productsSnapshot.forEach((doc) => {
      const productData = doc.data();
      
      // Filter by creation date if year and month are provided
      if (productData.createdAt) {
        const createdAt = productData.createdAt instanceof Date 
          ? productData.createdAt 
          : new Date((productData.createdAt as any).seconds * 1000);
        
        // Only include products created on or before the selected month
        const endOfSelectedMonth = new Date(selectedYear, selectedMonth + 1, 0); // Last day of selected month
        if (createdAt > endOfSelectedMonth) {
          return; // Skip this product if created after the selected month
        }
      }
      
      products.push({
        id: doc.id,
        ...productData
      });
    });
    
    // First try to get the average costs from the monthly inventory state (COGS)
    let cogsAverageCosts: Record<string, number> = {};
    
    try {
      // Check if monthly inventory state exists for the selected month
      const stateId = `${selectedYear}-${selectedMonth}`;
      const stateRef = doc(db, 'monthlyInventoryState', stateId);
      const stateSnap = await getDoc(stateRef);
      
      if (stateSnap.exists()) {
        const monthlyState = stateSnap.data();
        
        // Create a map of product IDs to their average costs from COGS
        if (monthlyState.products && Array.isArray(monthlyState.products)) {
          monthlyState.products.forEach((product: any) => {
            if (product.productId && product.averageCost !== undefined) {
              cogsAverageCosts[product.productId] = product.averageCost;
            }
          });
        }
      }
    } catch (error) {
      console.warn('Could not get average costs from monthly inventory state:', error);
    }
    
    // Fallback to product costs collection if COGS data is not available
    const productCosts = await getAllProductCosts();
    
    // Map product costs by product ID for faster lookup
    const productCostMap: Record<string, ProductCost> = {};
    productCosts.forEach((cost) => {
      productCostMap[cost.productId] = cost;
    });
    
    // Calculate inventory valuation
    const items = products.map((product) => {
      // First try to get average cost from COGS, then fall back to product costs
      let averageCost = 0;
      
      if (cogsAverageCosts[product.id] !== undefined) {
        // Use average cost from COGS if available
        averageCost = cogsAverageCosts[product.id];
      } else {
        // Fall back to product costs collection
        const cost = productCostMap[product.id] || null;
        averageCost = cost ? cost.averageCost : 0;
      }
      
      const quantity = product.stock || 0;
      const totalValue = quantity * averageCost;
      
      return {
        productId: product.id,
        productName: product.name,
        productSku: product.sku,
        quantity,
        averageCost,
        totalValue
      };
    });
    
    // Calculate total cost
    const totalCost = items.reduce((sum, item) => sum + item.totalValue, 0);
    
    return { totalCost, items };
  } catch (error) {
    console.error('Error getting inventory valuation:', error);
    throw error;
  }
};
